import { generateUid, sampleRandom } from '@gametailors/web-core/misc';
import {
  LpRidLeeslijstAssignment,
  LpRidLeeslijstConfig,
  LpRidWisselklankenAssignment,
  LpRidWisselklankenConfig,
  LpRIDWoord,
  LpTopicFilters,
} from '../../typings/schema';
import { AnnotationUtils } from '../utils/annotation.utils';

// The content of an assignment for a game played without a backend, chosen from the words of the data package. A
// smaller version of what the wisselklanken_assignment and leeslijst_assignment generators do: topic filters only
// match the `thema` field or '*', not topics.

const WORDS_PER_LEESLIJST_COLUMN = 6;
const LEESLIJST_COLUMNS = 3;

function filterByThema(words: LpRIDWoord[], topicFilters: LpTopicFilters): LpRIDWoord[] {
  const filter = topicFilters.filters[topicFilters.index];
  return words.filter((word) =>
    filter.groups.every((group) => group.strings.some((topic) => topic.string === '*' || topic.string === (word.thema ?? ''))),
  );
}

export function generateOfflineWisselklankenAssignment(
  config: LpRidWisselklankenConfig,
  words: LpRIDWoord[],
): LpRidWisselklankenAssignment {
  // configs saved before `unit` existed switch klanken
  const unit = config.unit ?? 'KLANK';
  const eligibleWords = words.filter(
    // a single klankvoet would be a single tile, which leaves nothing to switch between
    (w) => w.audio && w.image && w.thema && (unit === 'KLANK' || AnnotationUtils.parse(w.annotatie).length > 1),
  );
  const picked = sampleRandom(filterByThema(eligibleWords, config.topicFilters), config.count, true);
  if (picked.length === 0) {
    throw new Error(`Wisselklanken: no words with audio, image and klankvoeten for unit ${unit} match this config`);
  }
  return { uid: generateUid(), dataType: 'RidWisselklankenAssignment', words: picked, unit };
}

export function generateOfflineLeeslijstAssignment(config: LpRidLeeslijstConfig, words: LpRIDWoord[]): LpRidLeeslijstAssignment {
  const eligibleWords = filterByThema(
    words.filter((w) => !!w.thema),
    config.mode.topicFilters,
  );
  const wordList = sampleRandom(eligibleWords, WORDS_PER_LEESLIJST_COLUMN * LEESLIJST_COLUMNS, true);
  if (wordList.length === 0) {
    throw new Error('Leeslijst: no words match this config');
  }
  const columns = Array.from({ length: LEESLIJST_COLUMNS }, (_, i) => ({
    uid: generateUid(),
    dataType: 'RidWords' as const,
    words: wordList.slice(i * WORDS_PER_LEESLIJST_COLUMN, (i + 1) * WORDS_PER_LEESLIJST_COLUMN),
  }));

  const mode = config.mode;
  switch (mode.dataType) {
    case 'RidLeeslijstConfigModeGeenTijd':
      return {
        uid: generateUid(),
        dataType: 'RidLeeslijstAssignment',
        mode: { uid: generateUid(), dataType: 'RidLeeslijstAssignmentModeGeenTijd', words: columns, previousAccuracy: 1 },
      };
    case 'RidLeeslijstConfigModeStopwatch':
      return {
        uid: generateUid(),
        dataType: 'RidLeeslijstAssignment',
        mode: {
          uid: generateUid(),
          dataType: 'RidLeeslijstAssignmentModeStopwatch',
          words: columns,
          previousDuration: 10,
          previousAccuracy: 1,
        },
      };
    case 'RidLeeslijstConfigModeTimer':
      return {
        uid: generateUid(),
        dataType: 'RidLeeslijstAssignment',
        mode: {
          uid: generateUid(),
          dataType: 'RidLeeslijstAssignmentModeTimer',
          words: columns,
          previousAccuracy: 1,
          duration: mode.duration,
          previousWordCount: 1,
        },
      };
  }
}
